import _ from "lodash";

import {
  DestinationStandardMapping,
  DestinationStandardMappingItemProperty,
} from "../types";

const contentsFacebookProperties: DestinationStandardMappingItemProperty[] = [
  {
    key: "content_ids",
    exampleValue: "['ABC123', 'XYZ789']",
    type: "array<string | int>",
    description: "Product IDs associated with the event, such as SKUs",
  },
  {
    key: "contents",
    exampleValue: [
      { id: "ABC123", quantity: 2 },
      { id: "XYZ789", quantity: 2 },
    ],
    type: "array",
    description:
      "An array of JSON objects that contains the quantity and the International Article Number (EAN)" +
      " when applicable, or other product or content identifier(s).",
  },
];

const dynamicAdFacebookProperties: DestinationStandardMappingItemProperty[] = _.concat(
  contentsFacebookProperties,
  [
    {
      key: "content_type",
      exampleValue: "product",
      allowedValues: ["product", "product_group"],
      type: "string",
      description:
        "Either product or product_group based on the content_ids or contents being passed. If the IDs" +
        " being passed in content_ids or contents parameter are IDs of products then the value should be product. If product group IDs are being passed, then the value should be product_group.",
    },
  ]
);

const contentCategoryFacebookProperties: DestinationStandardMappingItemProperty[] = [
  {
    key: "content_category",
    exampleValue: "tech",
    type: "string",
    description: "Category of the page/product.",
  },
];

const contentNameFacebookProperties: DestinationStandardMappingItemProperty[] = [
  {
    key: "content_name",
    exampleValue: "USB  2m Cable",
    type: "string",
    description: "Name of the page/product.",
  },
];

const valueMetadataFacebookProperties: DestinationStandardMappingItemProperty[] = [
  {
    key: "currency",
    exampleValue: "USD",
    type: "string",
    description: "The currency for the value specified.",
    additionalTransformation: {
      operation: "uppercasePropertyValue",
    },
  },
  {
    key: "value",
    exampleValue: 45.5,
    type: "number",
    description: "The value of a user performing this event to the business.",
    additionalTransformation: {
      operation: "castPropertyValue",
      operandValue: "number",
    },
  },
];

const numberItemsFacebookProperty: DestinationStandardMappingItemProperty[] = [
  {
    key: "num_items",
    exampleValue: 3,
    type: "number",
    description:
      "Used with InitiateCheckout event. The number of items when checkout was initiated.",
    additionalTransformation: {
      operation: "castPropertyValue",
      operandValue: "number",
    },
  },
];

const predictedValueFacebookProperty: DestinationStandardMappingItemProperty[] = [
  {
    key: "predicted_ltv",
    exampleValue: 2450.0,
    type: "number",
    description:
      "Predicted lifetime value of a subscriber as defined by the advertiser and expressed as an" +
      " exact value.",
    additionalTransformation: {
      operation: "castPropertyValue",
      operandValue: "number",
    },
  },
];

const searchStringFacebookProperty: DestinationStandardMappingItemProperty[] = [
  {
    key: "search_string",
    exampleValue: "tiger prawns",
    type: "string",
    description:
      "Used with the Search event. The string entered by the user for the search.",
  },
];

const statusFacebookProperty: DestinationStandardMappingItemProperty[] = [
  {
    key: "status",
    exampleValue: true,
    type: "boolean",
    description:
      "Used with the CompleteRegistration event, to show the status of the registration.",
  },
];

export const facebookStandardMapping: DestinationStandardMapping = {
  wildcard: {
    description: "(Legacy) Pixel ID",
    propertiesArr: [],
  },
  items: [
    {
      eventName: "AddPaymentInfo",
      description: "When payment information is added in the checkout flow.",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "AddToCart",
      description: "When a product is added to the shopping cart.",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        contentCategoryFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "AddToWishlist",
      description: "When a product is added to a wishlist.",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        contentNameFacebookProperties,
        contentCategoryFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "CompleteRegistration",
      description: "When a registration form is completed.",
      propertiesArr: _.concat(
        contentNameFacebookProperties,
        valueMetadataFacebookProperties,
        statusFacebookProperty
      ),
    },
    {
      eventName: "Contact",
      description:
        "When a person initiates contact with your business via telephone, SMS, email, chat, etc.",
      propertiesArr: [],
    },
    {
      eventName: "CustomizeProduct",
      description: "When a person customizes a product.",
      propertiesArr: [],
    },
    {
      eventName: "Donate",
      description: "When a person donates funds to your organization or cause.",
      propertiesArr: [],
    },
    {
      eventName: "FindLocation",
      description:
        "When a person searches for a location of your store via a website or app, with an intention to visit the physical location.",
      propertiesArr: [],
    },
    {
      eventName: "InitiateCheckout",
      description:
        "When a person enters the checkout flow prior to completing the checkout flow.",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        numberItemsFacebookProperty,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "Lead",
      description: "When a sign up is completed.",
      propertiesArr: _.concat(
        contentCategoryFacebookProperties,
        contentNameFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "PageView",
      description: "This is the default pixel tracking page visits.",
      propertiesArr: [],
    },
    {
      eventName: "Purchase",
      description: "When a purchase is made or checkout flow is completed.",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        contentNameFacebookProperties,
        numberItemsFacebookProperty,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "Schedule",
      description:
        "When a person books an appointment to visit one of your locations.",
      propertiesArr: [],
    },
    {
      eventName: "Search",
      description: "When a search is made.",
      propertiesArr: _.concat(
        searchStringFacebookProperty,
        contentsFacebookProperties,
        contentCategoryFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
    {
      eventName: "StartTrial",
      description:
        "When a person starts a free trial of a product or service you offer.",
      propertiesArr: _.concat(
        valueMetadataFacebookProperties,
        predictedValueFacebookProperty
      ),
    },
    {
      eventName: "SubmitApplication",
      description:
        "When a person applies for a product, service, or program you offer.",
      propertiesArr: [],
    },
    {
      eventName: "Subscribe",
      description:
        "When a person applies to a start a paid subscription for a product or service you offer.",
      propertiesArr: _.concat(
        valueMetadataFacebookProperties,
        predictedValueFacebookProperty
      ),
    },
    {
      eventName: "ViewContent",
      description:
        "A visit to a web page you care about (for example, a product page or landing page).",
      propertiesArr: _.concat(
        dynamicAdFacebookProperties,
        contentCategoryFacebookProperties,
        contentNameFacebookProperties,
        valueMetadataFacebookProperties
      ),
    },
  ],
};

export const getFacebookStandardMapping = async () => {
  return facebookStandardMapping;
};
